import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { completeOnboarding } from '../../store/slices/authSlice';
import { onboardingService, AvailableSKU } from '../../services/onboarding.service';
import { Spinner } from '../../components/common/Spinner';

/**
 * Format number with appropriate suffix (K, M, etc.) or "Unlimited"
 */
const formatLimit = (value: number): string => {
  if (value === -1) return 'Unlimited';
  if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toString();
};

/**
 * Format storage size in MB/GB
 */
const formatStorage = (mb: number): string => {
  if (mb === -1) return 'Unlimited';
  if (mb >= 1024) return `${(mb / 1024).toFixed(0)} GB`;
  return `${mb} MB`;
};

/**
 * Plan Card Component
 */
interface PlanCardProps {
  plan: AvailableSKU;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: () => void;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, isSelected, isLoading, onSelect }) => {
  const isPro = plan.pricing.monthly > 0;

  return (
    <div
      className={`relative rounded-2xl border-2 transition-all duration-200 ${
        isSelected
          ? 'border-primary-500 ring-2 ring-primary-500 ring-offset-2'
          : 'border-gray-200 hover:border-primary-300'
      } bg-white p-6 shadow-sm hover:shadow-md`}
    >
      {/* Popular badge for paid plans */}
      {isPro && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center rounded-full bg-gradient-to-r from-primary-500 to-purple-500 px-3 py-1 text-xs font-semibold text-white">
            Popular
          </span>
        </div>
      )}

      {/* Plan header */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
        {plan.description && <p className="mt-2 text-sm text-gray-500">{plan.description}</p>}
      </div>

      {/* Pricing */}
      <div className="text-center mb-6">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-4xl font-bold text-gray-900">${plan.pricing.monthly}</span>
          <span className="text-gray-500">/month</span>
        </div>
        {plan.pricing.yearly && (
          <p className="mt-1 text-sm text-green-600">
            Save ${(plan.pricing.monthly * 12 - plan.pricing.yearly).toFixed(0)}/year with annual
            billing
          </p>
        )}
      </div>

      {/* Features list */}
      <ul className="space-y-3 mb-6">
        <li className="flex items-center gap-2 text-sm text-gray-600">
          <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
          <span>
            <strong>{formatLimit(plan.limits.maxBrands)}</strong> Brands
          </span>
        </li>
        <li className="flex items-center gap-2 text-sm text-gray-600">
          <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
          <span>
            <strong>{formatLimit(plan.limits.maxDeals)}</strong> Deals
          </span>
        </li>
        <li className="flex items-center gap-2 text-sm text-gray-600">
          <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
          <span>
            <strong>{formatLimit(plan.limits.maxDeliverablesPerDeal)}</strong> Deliverables per deal
          </span>
        </li>
        <li className="flex items-center gap-2 text-sm text-gray-600">
          <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
          <span>
            <strong>{formatStorage(plan.limits.maxTotalStorage)}</strong> Storage
          </span>
        </li>
        <li className="flex items-center gap-2 text-sm text-gray-600">
          <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
          <span>
            Max file size: <strong>{formatStorage(plan.limits.maxFileSize)}</strong>
          </span>
        </li>

        {/* Feature flags */}
        {plan.limits.features.aiContentGeneration && (
          <li className="flex items-center gap-2 text-sm text-gray-600">
            <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span>AI Content Generation</span>
          </li>
        )}
        {plan.limits.features.analytics && (
          <li className="flex items-center gap-2 text-sm text-gray-600">
            <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span>Advanced Analytics</span>
          </li>
        )}
        {plan.limits.features.prioritySupport && (
          <li className="flex items-center gap-2 text-sm text-gray-600">
            <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span>Priority Support</span>
          </li>
        )}

        {/* Video uploads */}
        <li className="flex items-center gap-2 text-sm text-gray-600">
          {plan.limits.allowedFileTypes.videos ? (
            <>
              <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>Video Uploads</span>
            </>
          ) : (
            <>
              <XIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <span className="text-gray-400">Video Uploads</span>
            </>
          )}
        </li>
      </ul>

      {/* Select button */}
      <button
        onClick={onSelect}
        disabled={isLoading}
        className={`w-full rounded-lg px-4 py-3 font-semibold transition-all duration-200 ${
          isPro
            ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white hover:from-primary-700 hover:to-purple-700 shadow-md hover:shadow-lg'
            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <Spinner size="sm" />
            Selecting...
          </span>
        ) : (
          `Select ${plan.name}`
        )}
      </button>
    </div>
  );
};

/**
 * Check icon component
 */
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * X icon component
 */
const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/**
 * Select Plan Page Component
 */
const SelectPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [plans, setPlans] = useState<AvailableSKU[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch available plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const availablePlans = await onboardingService.getAvailableSKUs();
        // Sort by display order (copy — never mutate the service return)
        setPlans([...availablePlans].sort((a, b) => a.displayOrder - b.displayOrder));
      } catch (err: any) {
        setError(err.message || 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Handle plan selection
  const handleSelectPlan = async (planId: string) => {
    try {
      setSelecting(planId);
      setError(null);
      await onboardingService.selectSKU(planId);
      dispatch(completeOnboarding());
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to select plan');
      setSelecting(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading available plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Select the plan that best fits your needs. You can always upgrade later.
          </p>

          {/* Logo/Brand */}
          <div className="mt-6 flex items-center justify-center gap-3">
            <img src="/logo.svg" alt="Orbit Logo" className="size-10" />
            <img src="/orbit_text.svg?v=4" alt="Orbit" className="h-8 w-auto" />
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
            {error}
          </div>
        )}

        {/* Plans grid or empty state */}
        {plans.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center max-w-2xl mx-auto">
            {/* Empty state icon */}
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary-100 to-purple-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>

            {/* Empty state messaging */}
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Plans Are Being Set Up</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Our team is currently configuring the available subscription plans. Please check back
              shortly or contact support for assistance.
            </p>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Refresh Page
              </button>
              <a
                href="mailto:support@mindalyx.com"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-all duration-200"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Contact Support
              </a>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-8 justify-center mx-auto max-w-6xl">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)]"
              >
                <PlanCard
                  plan={plan}
                  isSelected={selecting === plan.id}
                  isLoading={selecting === plan.id}
                  onSelect={() => handleSelectPlan(plan.id)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Footer note - hide on empty (Plans being set up) state to avoid redundancy */}
        {plans.length > 0 && (
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500">
              Have questions?{' '}
              <a href="mailto:support@mindalyx.com" className="text-primary-600 hover:underline">
                Contact us
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectPlanPage;
